import { MenuItem, Paper, Stack, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useQuery } from '@tanstack/react-query';
import type { Dayjs } from 'dayjs';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { listSentDocuments } from '../../api/endpoints';
import { gateOffsetForNewLimit } from '../../api/paging';
import { queryKeys } from '../../api/queryKeys';
import { flowisDefaultLimit, flowisSentStatuses, type FlowisDocumentQuery } from '../../api/types';
import { gateStatusLabel } from '../../components/statusLabels';
import { useSelectedTenant } from '../../tenant/tenantSelection';
import { FlowisDocumentDialog } from './FlowisDocumentDialog';
import { FlowisDocumentsTable } from './FlowisDocumentsTable';

export function SentTab({ accountGid }: { accountGid: string }) {
  const { t } = useTranslation();
  const { selectedTenantGid } = useSelectedTenant();
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(flowisDefaultLimit);
  const [status, setStatus] = useState('');
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  const query = useMemo<FlowisDocumentQuery>(
    () => ({
      offset,
      limit,
      ...(status ? { status } : {}),
      // The gate takes plain dates, not instants.
      ...(fromDate?.isValid() ? { fromDate: fromDate.format('YYYY-MM-DD') } : {}),
      ...(toDate?.isValid() ? { toDate: toDate.format('YYYY-MM-DD') } : {}),
    }),
    [offset, limit, status, fromDate, toDate],
  );

  const documents = useQuery({
    queryKey: queryKeys.flowis.sent(selectedTenantGid, accountGid, query),
    queryFn: ({ signal }) => listSentDocuments(accountGid, query, signal),
    enabled: accountGid.length > 0,
  });

  return (
    <Stack spacing={2}>
      <Paper sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            select
            size="small"
            label={t('columns.status')}
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              setOffset(0);
            }}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">{t('flowis.filters.anyStatus')}</MenuItem>
            {flowisSentStatuses.map((value) => (
              <MenuItem key={value} value={value}>
                {gateStatusLabel(value, t)}
              </MenuItem>
            ))}
          </TextField>
          <DatePicker
            label={t('flowis.filters.issuedFrom')}
            value={fromDate}
            onChange={(value) => {
              setFromDate(value);
              setOffset(0);
            }}
            slotProps={{ textField: { size: 'small' }, field: { clearable: true } }}
          />
          <DatePicker
            label={t('flowis.filters.issuedTo')}
            value={toDate}
            onChange={(value) => {
              setToDate(value);
              setOffset(0);
            }}
            slotProps={{ textField: { size: 'small' }, field: { clearable: true } }}
          />
        </Stack>
      </Paper>

      <FlowisDocumentsTable
        page={documents.data}
        isPending={documents.isPending}
        error={documents.error}
        onRetry={() => void documents.refetch()}
        onSelect={setSelected}
        offset={offset}
        limit={limit}
        onOffsetChange={setOffset}
        onLimitChange={(next) => {
          setOffset(gateOffsetForNewLimit(offset, next));
          setLimit(next);
        }}
      />

      <FlowisDocumentDialog
        accountGid={accountGid}
        direction="sent"
        documentId={selected}
        onClose={() => setSelected(null)}
      />
    </Stack>
  );
}
